import { useModal } from '@/contexts/modal'
import { useTourHandler } from '@/contexts/tour-handler'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'
import AddIcons from '@/icons/AddIcons'
import { AddCustomerJourneyProps } from '@/modals/CustomerModals/AddCustomerJourney'
import { CUSTOMER_JOURNEYS } from '@/modules/Customer/constants'
import {
    createCustomerJourney,
    getCustomerJourney,
} from '@/modules/Customer/services'
import { CustomerJourney } from '@/modules/Customer/types'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { Button, Loading, Typogaphy } from 'vnetwork-ui'
import { useInfiniteQueryWithHandler } from '@/hooks/useInfiniteQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import SaleProcessBoard from './SaleProcessBoard'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useSaleProcessTour } from './useSaleProcessTour'
import { useCustomerReadOnly } from '../CustomerReadOnlyContext'

const SaleProcess = () => {
    const { id } = useParams()
    const { setModal } = useModal()
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const { registerHandler, unregisterHandler } = useTourHandler()
    const { startTour } = useSaleProcessTour()
    const isReadOnly = useCustomerReadOnly()

    // Quick Help options for this page
    const quickHelpOptions: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'add-journey',
                label: 'Tạo quá trình bán hàng mới',
                description: 'Thêm Journey hoặc Upsell',
                selector: '[data-tour="add-journey-btn"]',
                tourTitle: 'Thêm Quá Trình Mới',
                tourDescription:
                    'Click vào đây để tạo quá trình bán hàng mới (Journey) hoặc Upsell cho khách hàng.',
            },
            {
                id: 'edit-journey',
                label: 'Chỉnh sửa thông tin hành trình',
                description: 'Cập nhật Sales, ngày dự kiến chốt',
                selector: '[data-tour="edit-journey-info-btn"]',
                tourTitle: 'Chỉnh Sửa Thông Tin',
                tourDescription:
                    'Click để mở panel chỉnh sửa: ngày dự kiến chốt, ghi chú, thêm/bớt Sales phụ trách.',
            },
            {
                id: 'create-business-trip',
                label: 'Tạo phiếu công tác',
                description: 'Đi gặp khách hàng trực tiếp',
                selector: '[data-tour="create-business-trip-btn"]',
                tourTitle: 'Tạo Phiếu Công Tác',
                tourDescription:
                    'Tạo phiếu công tác để đi gặp khách hàng. Cần chuyển đến bước Meeting/Presentation trước.',
            },
            {
                id: 'create-meeting',
                label: 'Tạo Meeting Online',
                description: 'Họp với khách từ xa',
                selector: '[data-tour="create-meeting-btn"]',
                tourTitle: 'Tạo Meeting Online',
                tourDescription:
                    'Tạo ticket meeting online (Google Meet, Zoom...). Cần ở bước Meeting/Presentation.',
            },
            {
                id: 'create-poc',
                label: 'Mở POC cho khách hàng',
                description: 'Tạo dịch vụ dùng thử',
                selector: '[data-tour="create-poc-btn"]',
                tourTitle: 'Tạo Dịch Vụ POC',
                tourDescription:
                    'Tạo dịch vụ POC (Proof of Concept) cho khách dùng thử. Cần ở bước POC.',
            },
            {
                id: 'add-negotiation',
                label: 'Thêm thương lượng giá',
                description: 'Ghi nhận đàm phán với khách',
                selector: '[data-tour="add-negotiation-btn"]',
                tourTitle: 'Thêm Thương Lượng',
                tourDescription:
                    'Ghi nhận thương lượng giá: giá đề xuất, giá khách yêu cầu. Cần ở bước Hợp đồng.',
            },
            {
                id: 'create-contract',
                label: 'Tạo hợp đồng mới',
                description: 'Ký hợp đồng với khách',
                selector: '[data-tour="create-contract-btn"]',
                tourTitle: 'Tạo Hợp Đồng',
                tourDescription:
                    'Tạo hợp đồng mới. Sau khi ký, hành trình tự động chuyển sang Won. Cần ở bước Hợp đồng.',
            },
            {
                id: 'add-note',
                label: 'Thêm ghi chú',
                description: 'Ghi nhận nội dung trao đổi',
                selector: '[data-tour="add-note-btn"]',
                tourTitle: 'Thêm Ghi Chú',
                tourDescription:
                    'Ghi chú nội dung trao đổi, follow up. Có thể thêm ở bất kỳ bước nào.',
            },
        ],
        []
    )

    // Register quick help options for this page
    useQuickHelp(quickHelpOptions)

    // Register tour handler for keyboard shortcut
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    const getContactId = () => {
        return searchParams.get('contact_id')
            ? Number(searchParams.get('contact_id'))
            : null
    }

    const {
        data,
        renderState,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
    } = useInfiniteQueryWithHandler<any, Error, any, any, number>(
        {
            queryKey: [QUERY_KEYS.CUSTOMERS, 'journeys', id, getContactId()],
            queryFn: ({ pageParam = 1 }) =>
                getCustomerJourney({
                    customer_id: Number(id),
                    params: {
                        page: Number(pageParam),
                        page_size: 20,
                        q: '',
                        contact_id: getContactId(),
                    },
                }),
            initialPageParam: 1,
            getNextPageParam: (lastPage: any) => {
                return lastPage.pagination?.page_next === -1
                    ? undefined
                    : lastPage.pagination?.page_next
            },
        },
        {
            loadingComponent: (
                <div className="p-8 flex justify-center">
                    <Loading.CircularProgress width={32} height={32} />
                </div>
            ),
        }
    )

    const journeys = useMemo(() => {
        const allJourneys =
            (data?.pages?.reduce((final: CustomerJourney[], page: any) => {
                final.push(...(page?.results || []))
                return final
            }, [] as CustomerJourney[]) as CustomerJourney[]) || []

        // Sort: Active journeys first (by last update desc), then completed journeys (by last update desc)
        return allJourneys.sort((a, b) => {
            // Active (unlocked) journeys come first
            if (!a.customer_journey_locked && b.customer_journey_locked)
                return -1
            if (a.customer_journey_locked && !b.customer_journey_locked)
                return 1

            // Both same status - sort by last update (most recent first)
            const aTime =
                a.customer_journey_explaination?.explaination_updated_at ||
                a.customer_journey_created_at
            const bTime =
                b.customer_journey_explaination?.explaination_updated_at ||
                b.customer_journey_created_at
            return new Date(bTime).getTime() - new Date(aTime).getTime()
        })
    }, [data])

    // Mutation để tạo journey mới
    const { isPending: isCreatingJourney } = useMutationWithHandler(
        { mutationFn: createCustomerJourney },
        {
            reactQueryKeys: [
                QUERY_KEYS.CUSTOMERS,
                QUERY_KEYS.CUSTOMERS_JOURNEYS,
            ],
            successMessage: t('journey-created-successfully'),
            errorMessage: t('failed-to-create-journey'),
        }
    )

    const journeySteps = useMemo(() => {
        return CUSTOMER_JOURNEYS(t).filter(
            (step) => step.value !== 'closed' && step.value !== 'upsale'
        )
    }, [t])

    // Helper to check if journey is won
    const isJourneyWon = (journey: CustomerJourney) => {
        return (
            journey.customer_journey_locked &&
            (journey.customer_journey_step === 'close_win' ||
                (journey.customer_journey_step === 'contract' &&
                    journey.customer_journey_step_status === 'completed'))
        )
    }

    // Always allow creating new journeys
    const canCreateNewJourney = true

    // Find primary journey index for upsell detection
    const primaryJourneyIndex = useMemo(() => {
        return journeys.findIndex(isJourneyWon)
    }, [journeys])

    // Check if there's any successful journey (for determining new sale vs upsell)
    const hasSuccessfulJourney = primaryJourneyIndex !== -1

    const isUpsellJourney = (journeyIndex: number): boolean => {
        if (primaryJourneyIndex === -1) return false
        return journeyIndex > primaryJourneyIndex
    }

    // Check if next journey will be upsell
    const isNextJourneyUpsell = hasSuccessfulJourney

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="space-y-2 mt-2">
            {/* Empty State */}
            {journeys.length === 0 ? (
                <div className="bg-white rounded-md border border-gray-200 p-8 md:p-12 text-center">
                    <div className="max-w-sm mx-auto">
                        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                            <svg
                                className="w-8 h-8 text-gray-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                />
                            </svg>
                        </div>
                        <Typogaphy className="text-lg font-medium text-gray-800 mb-2">
                            {t('no-sales-journey-yet')}
                        </Typogaphy>
                        <Typogaphy className="text-sm text-gray-500 mb-6">
                            {t('start-tracking-customer-progress')}
                        </Typogaphy>
                        {!isReadOnly && (
                            <Button
                                variant="contained"
                                startIcon={<AddIcons />}
                                onClick={() =>
                                    setModal<AddCustomerJourneyProps>(
                                        'CustomerModals/AddCustomerJourney/index',
                                        {
                                            customerId: Number(id),
                                            contact_id:
                                                Number(
                                                    searchParams.get(
                                                        'contact_id'
                                                    )
                                                ) || 0,
                                            isUpsell: false,
                                            hasSuccessfulJourney: false,
                                        }
                                    )
                                }
                                className="bg-gray-800 hover:bg-gray-900"
                            >
                                {t('create-first-journey')}
                            </Button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    {/* Add Journey Button - Top Right */}
                    {canCreateNewJourney && !isReadOnly && (
                        <div className="flex justify-end">
                            <Button
                                variant="outlined"
                                startIcon={<AddIcons />}
                                onClick={() =>
                                    setModal<AddCustomerJourneyProps>(
                                        'CustomerModals/AddCustomerJourney/index',
                                        {
                                            customerId: Number(id),
                                            contact_id:
                                                Number(
                                                    searchParams.get(
                                                        'contact_id'
                                                    )
                                                ) || 0,
                                            isUpsell: isNextJourneyUpsell,
                                            hasSuccessfulJourney,
                                        }
                                    )
                                }
                                className="border-gray-300 text-gray-600 hover:bg-gray-50"
                                disabled={isCreatingJourney}
                                data-tour="add-journey-btn"
                            >
                                {isNextJourneyUpsell
                                    ? t('add-upsell-journey')
                                    : t('add-customer-journey')}
                            </Button>
                        </div>
                    )}

                    {/* Journey Boards */}
                    {journeys.map((journey, index) => (
                        <SaleProcessBoard
                            key={journey.customer_journey_id}
                            journey={journey}
                            journeySteps={journeySteps}
                            customerId={Number(id)}
                            contactId={
                                Number(searchParams.get('contact_id')) || 0
                            }
                            journeyIndex={index}
                            isUpsell={isUpsellJourney(index)}
                        />
                    ))}

                    {/* Load More */}
                    {hasNextPage && (
                        <div className="text-center">
                            <Button
                                variant="text"
                                onClick={() => fetchNextPage()}
                                disabled={isFetchingNextPage}
                                loading={isFetchingNextPage}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                {t('load-more-journeys')}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default SaleProcess
